import React, { useRef, useState } from "react";
import { StyleSheet, Text, TextInput as TextInputRN, View } from "react-native";
import { Button, Snackbar, TextInput, HelperText } from "react-native-paper";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

type Props = {
  onNavigateForgot: () => void;
  onNavigateRegister: () => void;
  version: string;
  signInFn: (email: string, password: string) => Promise<any>;
};

const loginSchema = z.object({
  email: z.email({ message: "Invalid email address" }),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginComponent({
  onNavigateForgot,
  onNavigateRegister,
  version,
  signInFn,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState("");

  const passwordRef = useRef<TextInputRN>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const signIn = async (data: LoginForm) => {
    setLoading(true);
    setAuthError("");
    try {
      await signInFn(data.email, data.password);
    } catch (error: any) {
      console.error(error);
      const friendly = t(error?.code) || t("Authentication failed");
      setAuthError(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.formContainer}>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                testID={"Email"}
                autoCapitalize="none"
                value={value}
                onChangeText={onChange}
                label={t("Email")}
                mode="outlined"
                keyboardType="email-address"
                textContentType={"emailAddress"}
                onSubmitEditing={() => passwordRef.current?.focus()}
                autoFocus
                returnKeyType="next"
                accessibilityLabel={t("Email")}
              />
              <HelperText type="error" visible={!!errors.email}>
                {errors.email?.message}
              </HelperText>
            </>
          )}
        />

        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                ref={passwordRef}
                testID={"Password"}
                secureTextEntry={!showPassword}
                right={
                  <TextInput.Icon
                    onPress={() => setShowPassword((prev) => !prev)}
                    icon={showPassword ? "eye" : "eye-off"}
                    accessibilityLabel={
                      showPassword ? t("Hide password") : t("Show password")
                    }
                  />
                }
                value={value}
                onChangeText={onChange}
                label={t("Password")}
                textContentType={"password"}
                mode="outlined"
                onSubmitEditing={handleSubmit(signIn)}
                returnKeyType="done"
              />
              <HelperText type="error" visible={!!errors.password}>
                {errors.password?.message}
              </HelperText>
            </>
          )}
        />

        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Button mode="text" disabled={loading} onPress={onNavigateForgot}>
            {t("Forgot password?")}
          </Button>
          <Button
            testID={"navigate-sign-up"}
            mode="text"
            disabled={loading}
            onPress={onNavigateRegister}
          >
            {t("Sign up")}
          </Button>
        </View>

        <Button
          testID={"sign-in"}
          mode="contained"
          loading={loading}
          disabled={loading}
          style={styles.signInButton}
          onPress={handleSubmit(signIn)}
        >
          {t("Sign in")}
        </Button>

        <Text style={styles.versionText}>v{version}</Text>

        <Snackbar visible={!!authError} onDismiss={() => setAuthError("")}>
          {authError}
        </Snackbar>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignItems: "center",
  },
  formContainer: {
    maxWidth: 400,
    width: "100%",
  },
  input: {
    // marginBottom: 15,
  },
  errorText: {
    color: "#B00020",
    marginBottom: 8,
    fontSize: 12,
  },
  linkButton: {
    // borderRadius: 8,
    marginTop: 5,
  },
  signInButton: {
    // borderRadius: 8,
    marginTop: 15,
  },
  versionText: {
    textAlign: "center",
    marginTop: 20,
    color: "#666",
    fontSize: 12,
  },
});
